import { 
  doc as firestoreDoc, 
  getDoc, 
  updateDoc, 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  orderBy, 
  where, 
  Timestamp,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db, formatTimestamp } from './firebase';
import { formatDateBR, convertDateBRToISO } from '../utils/dateUtils';
import { format } from 'date-fns';
import { queryClient } from '../services/queryClient';

/**
 * Busca agendamentos do salão com filtros opcionais
 * @param {string} salonId - ID do salão
 * @param {object} filters - Filtros para a busca
 * @returns {Promise<Array>} - Lista de agendamentos
 */
export const getAppointments = async (salonId, filters = {}) => {
  try {
    // Removendo logs de depuração
    // console.log("[getAppointments] Buscando agendamentos para o salão:", salonId, "Filtros:", filters);
    
    // Verificar se as datas dos filtros são objetos Date ou strings
    // if (filters.startDate) {
    //   console.log(`[getAppointments] startDate é Date? ${filters.startDate instanceof Date}`, 
    //     filters.startDate instanceof Date ? 
    //       `${filters.startDate.toISOString()}` : 
    //       `Tipo: ${typeof filters.startDate}, Valor: ${filters.startDate}`
    //   );
    // }
    
    // if (filters.endDate) {
    //   console.log(`[getAppointments] endDate é Date? ${filters.endDate instanceof Date}`, 
    //     filters.endDate instanceof Date ? 
    //       `${filters.endDate.toISOString()}` : 
    //       `Tipo: ${typeof filters.endDate}, Valor: ${filters.endDate}`
    //   );
    // }
    
    // Flag que indica se estamos usando strings para o formato de data
    const useStringDateFormat = filters.stringDateFormat === true;
    // console.log(`[getAppointments] Usando formato de data em string? ${useStringDateFormat}`);
    
    let appointmentsQuery = collection(db, `salons/${salonId}/appointments`);
    
    const queryConstraints = [];
    
    // Aplicar filtros se fornecidos
    if (filters.startDate) {
      if (useStringDateFormat) {
        // Para datas em formato de string (yyyy-mm-dd)
        queryConstraints.push(where('data', '>=', filters.startDate));
      } else {
        // Para objetos Date
        queryConstraints.push(where('data', '>=', filters.startDate));
      }
    }
    
    if (filters.endDate) {
      if (useStringDateFormat) {
        // Para datas em formato de string (yyyy-mm-dd)
        queryConstraints.push(where('data', '<=', filters.endDate));
      } else {
        // Para objetos Date
        queryConstraints.push(where('data', '<=', filters.endDate));
      }
    }
    
    if (filters.confirmed !== undefined) {
      queryConstraints.push(where('confirmado', '==', filters.confirmed));
    }
    
    if (filters.messagesSent !== undefined) {
      queryConstraints.push(where('mensagemEnviada', '==', filters.messagesSent));
    }
    
    if (filters.canceled !== undefined) {
      queryConstraints.push(where('cancelado', '==', filters.canceled));
    }
    
    // Aplicar ordenação - para string ou date
    if (useStringDateFormat) {
      queryConstraints.push(orderBy('data', 'asc'));
    } else {
      queryConstraints.push(orderBy('data', 'desc'));
    }
    
    // Criar a consulta com os filtros
    appointmentsQuery = query(appointmentsQuery, ...queryConstraints);
    
    // Executar a consulta
    const snapshot = await getDocs(appointmentsQuery);
    // console.log("[getAppointments] Agendamentos encontrados:", snapshot.docs.length);
    
    // Buscar todos os serviços e profissionais de uma vez só para evitar múltiplas consultas
    let servicesData = [];
    let professionalsData = [];
    
    try {
      // Buscar serviços
      const servicesRef = collection(db, `salons/${salonId}/services`);
      const servicesSnapshot = await getDocs(servicesRef);
      servicesData = servicesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // console.log("Serviços encontrados:", servicesData.length);
      
      // Buscar profissionais
      const professionalsRef = collection(db, `salons/${salonId}/professionals`);
      const professionalsSnapshot = await getDocs(professionalsRef);
      professionalsData = professionalsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // console.log("Profissionais encontrados:", professionalsData.length);
    } catch (error) {
      console.error("Erro ao buscar serviços ou profissionais:", error);
    }
    
    // Lista de promessas para processar agendamentos
    const appointmentPromises = snapshot.docs.map(async (docSnapshot) => {
      try {
        const data = docSnapshot.data();
        const appointmentData = {
          id: docSnapshot.id,
          ...data,
          data: formatTimestamp(data.data)
        };
        
        // Verificar se os serviços são apenas IDs e buscar detalhes se necessário
        if (Array.isArray(appointmentData.servicos) && appointmentData.servicos.length > 0) {
          // Verificar se algum serviço é apenas um ID (string)
          const hasIdOnlyServices = appointmentData.servicos.some(service => 
            typeof service === 'string'
          );
          
          if (hasIdOnlyServices && servicesData.length > 0) {
            // Substituir IDs por objetos de serviço completos
            appointmentData.servicos = appointmentData.servicos.map(serviceId => {
              if (typeof serviceId === 'string') {
                const service = servicesData.find(s => s.id === serviceId);
                return service || { nome: 'Serviço desconhecido', id: serviceId };
              }
              return serviceId;
            });
          }
        }
        
        // Verificar se o profissional é apenas um ID e buscar detalhes se necessário
        if (appointmentData.profissional && typeof appointmentData.profissional === 'string') {
          // Buscar profissional nos dados já carregados
          const professional = professionalsData.find(p => p.id === appointmentData.profissional);
          
          if (professional) {
            appointmentData.profissional = professional;
          }
        }
        
        return appointmentData;
      } catch (error) {
        console.error("Erro ao processar documento de agendamento:", error);
        return null;
      }
    });
    
    // Aguardar todas as promessas concluírem
    const processedAppointments = await Promise.all(appointmentPromises);
    
    // Filtrar agendamentos nulos e retornar
    return processedAppointments.filter(appointment => appointment !== null);
  } catch (error) {
    console.error('Erro ao buscar agendamentos:', error);
    throw error;
  }
};

/**
 * Normaliza os dados de um agendamento para garantir consistência
 * @param {object} appointmentData - Dados brutos do agendamento
 * @returns {object} - Dados normalizados do agendamento
 */
export const normalizeAppointmentData = (appointmentData) => {
  // Criar cópia para não modificar o objeto original
  const normalizedData = { ...appointmentData };
  
  // Verificar se o campo nome foi usado em vez de cliente
  if (normalizedData.nome && !normalizedData.cliente) {
    normalizedData.cliente = normalizedData.nome;
    delete normalizedData.nome;
  }
  
  // Normalização de campos de data
  if (normalizedData.data) {
    // Verificar se a data está no formato brasileiro (DD/MM/YYYY)
    if (normalizedData.data.includes('/')) {
      // Se estiver em formato BR, precisamos normalizar para ISO
      normalizedData.dataFormatada = normalizedData.data; // Guardar a data formatada
      normalizedData.data = convertDateBRToISO(normalizedData.data); // Converter para ISO
    } else {
      // Se já estiver em formato ISO, gerar a versão formatada
      normalizedData.dataFormatada = formatDateBR(normalizedData.data);
    }
  }
  
  // Garantir que todos os formatos de data sejam consistentes
  if (normalizedData.dataString && !normalizedData.dataFormatada) {
    normalizedData.dataFormatada = normalizedData.dataString;
  }
  
  // Normalizar horário (garantir formato HH:MM)
  if (normalizedData.horario && normalizedData.horario.length > 5) {
    normalizedData.horario = normalizedData.horario.substring(0, 5);
  }
  
  // Garantir que origem seja especificada
  if (!normalizedData.origem) {
    normalizedData.origem = normalizedData.source || normalizedData.criadoPorAdmin ? 'admin' : 'cliente';
    
    // Remover campos redundantes
    if (normalizedData.source) delete normalizedData.source;
  }
  
  // Garantir que o dispositivo seja especificado
  if (!normalizedData.deviceType) {
    // Detectar dispositivo padrão se não for especificado
    normalizedData.deviceType = typeof navigator !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
      ? 'mobile' 
      : 'desktop';
  }
  
  // Garantir campos de status estejam presentes e sejam booleanos
  normalizedData.confirmado = !!normalizedData.confirmado;
  normalizedData.cancelado = !!normalizedData.cancelado;
  normalizedData.mensagemEnviada = !!normalizedData.mensagemEnviada;
  
  // Retornar objeto normalizado
  return normalizedData;
};

/**
 * Cria um novo agendamento
 * @param {string} salonId - ID do salão
 * @param {object} appointmentData - Dados do agendamento
 * @returns {Promise<object>} - Agendamento criado com ID
 */
export const createAppointment = async (salonId, appointmentData) => {
  try {
    // Normalizar dados do agendamento
    const normalizedData = normalizeAppointmentData(appointmentData);
    
    // Garantir que clienteId seja incluído se presente
    const appointmentToSave = {
      ...normalizedData,
      confirmado: normalizedData.confirmado || false,
      cancelado: normalizedData.cancelado || false,
      mensagemEnviada: normalizedData.mensagemEnviada || false,
      createdAt: new Date()
    };
    
    // Preservar o clienteId se estiver presente
    if (appointmentData.clienteId) {
      appointmentToSave.clienteId = appointmentData.clienteId;
    }
    
    const appointmentsRef = collection(db, `salons/${salonId}/appointments`);
    const docRef = await addDoc(appointmentsRef, appointmentToSave);
    
    return { 
      id: docRef.id, 
      ...normalizedData,
      clienteId: appointmentToSave.clienteId, // Garantir que retorne o clienteId
      confirmado: normalizedData.confirmado || false,
      cancelado: normalizedData.cancelado || false,
      mensagemEnviada: normalizedData.mensagemEnviada || false
    };
  } catch (error) {
    console.error('Erro ao criar agendamento:', error);
    throw error;
  }
};

/**
 * Confirma um agendamento
 * @param {string} salonId - ID do salão
 * @param {string} appointmentId - ID do agendamento
 * @returns {Promise<boolean>} - True se confirmado com sucesso
 */
export const confirmAppointment = async (salonId, appointmentId) => {
  try {
    const appointmentRef = firestoreDoc(db, `salons/${salonId}/appointments/${appointmentId}`);
    await updateDoc(appointmentRef, {
      confirmado: true,
      dataConfirmacao: Timestamp.now()
    });
    return true;
  } catch (error) {
    console.error('Erro ao confirmar agendamento:', error);
    throw error;
  }
};

/**
 * Cancela um agendamento
 * @param {string} salonId - ID do salão
 * @param {string} appointmentId - ID do agendamento
 * @returns {Promise<boolean>} - True se cancelado com sucesso
 */
export const cancelAppointment = async (salonId, appointmentId) => {
  try {
    const appointmentRef = firestoreDoc(db, `salons/${salonId}/appointments/${appointmentId}`);
    await updateDoc(appointmentRef, {
      confirmado: false,
      cancelado: true,
      dataCancelamento: Timestamp.now()
    });
    return true;
  } catch (error) {
    console.error('Erro ao cancelar agendamento:', error);
    throw error;
  }
};

/**
 * Marca um agendamento como tendo mensagem enviada
 * @param {string} salonId - ID do salão
 * @param {string} appointmentId - ID do agendamento
 * @returns {Promise<boolean>} - True se marcado com sucesso
 */
export const markAppointmentMessageSent = async (salonId, appointmentId) => {
  try {
    const appointmentRef = firestoreDoc(db, `salons/${salonId}/appointments/${appointmentId}`);
    await updateDoc(appointmentRef, {
      mensagemEnviada: true,
      dataEnvioMensagem: Timestamp.now()
    });
    return true;
  } catch (error) {
    console.error('Erro ao marcar mensagem como enviada:', error);
    throw error;
  }
};

/**
 * Busca agendamentos pendentes de confirmação
 * @param {string} salonId - ID do salão
 * @param {number} daysAhead - Número de dias à frente para buscar (padrão: 7)
 * @returns {Promise<Array>} - Lista de agendamentos pendentes
 */
export const getPendingConfirmations = async (salonId, daysAhead = 7) => {
  try {
    const appointmentsRef = collection(db, "salons", salonId, "appointments");
    
    // Calcular a data limite com base em daysAhead
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const limitDate = new Date(today);
    limitDate.setDate(limitDate.getDate() + daysAhead);
    
    // Formatar data limite para o formato usado no Firestore (YYYY-MM-DD)
    const limitDateStr = limitDate.toISOString().split('T')[0];
    
    console.log(`[getPendingConfirmations] Buscando confirmações pendentes até: ${limitDateStr}`);
    
    // Modificar a query para buscar agendamentos não confirmados onde cancelado é false
    // ou onde o campo cancelado não existe
    const q = query(
      appointmentsRef,
      where("confirmado", "==", false),       // Manter apenas não confirmados
      where("cancelado", "in", [false, null]), // Aceitar tanto false como não existente
      orderBy("data")                         // Ordenar por data
    );
    
    const snapshot = await getDocs(q);
    const appointments = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      
      // Log para depuração de filtros específicos
      if (data.data === "2025-03-17") {
        console.log('[getPendingConfirmations] Agendamento 17/03 filtrado como pendente?', 
          !data.confirmado && !data.cancelado, 
          { id: doc.id, mensagemEnviada: data.mensagemEnviada, cancelado: data.cancelado, motivo: (data.mensagemEnviada ? "mensagem já enviada" : "não enviada") }
        );
      }
      
      // Garantir que o campo cancelado exista
      const appointmentData = {
        id: doc.id,
        ...data,
        cancelado: data.cancelado === undefined ? false : data.cancelado
      };
      
      appointments.push(appointmentData);
    });
    
    return appointments;
  } catch (error) {
    console.error('Erro ao buscar confirmações pendentes:', error);
    throw error;
  }
};

/**
 * Atualiza um agendamento existente
 * @param {string} salonId - ID do salão
 * @param {string} appointmentId - ID do agendamento
 * @param {object} appointmentData - Novos dados do agendamento
 * @returns {Promise<object>} - Agendamento atualizado
 */
export const updateAppointment = async (salonId, appointmentId, appointmentData) => {
  try {
    // Normalizar dados antes de atualizar
    const normalizedData = normalizeAppointmentData(appointmentData);
    
    const appointmentRef = firestoreDoc(db, `salons/${salonId}/appointments/${appointmentId}`);
    await updateDoc(appointmentRef, {
      ...normalizedData,
      updatedAt: new Date()
    });
    return { id: appointmentId, ...normalizedData };
  } catch (error) {
    console.error('Erro ao atualizar agendamento:', error);
    throw error;
  }
};

/**
 * Remove um agendamento
 * @param {string} salonId - ID do salão
 * @param {string} appointmentId - ID do agendamento
 * @returns {Promise<boolean>} - True se removido com sucesso
 */
export const deleteAppointment = async (salonId, appointmentId) => {
  try {
    const appointmentRef = firestoreDoc(db, `salons/${salonId}/appointments/${appointmentId}`);
    await deleteDoc(appointmentRef);
    return true;
  } catch (error) {
    console.error('Erro ao excluir agendamento:', error);
    throw error;
  }
};

/**
 * Gera um token único para um agendamento
 * @param {string} appointmentId - ID do agendamento
 * @returns {string} Token gerado
 */
export const generateAppointmentToken = (appointmentId) => {
  // Gera um token único com 32 caracteres hexadecimais
  const randomToken = Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  
  // Adiciona timestamp para evitar colisão
  const timestamp = Date.now().toString(36);
  
  return `${randomToken}-${appointmentId}-${timestamp}`;
};

/**
 * Adiciona ou atualiza token em um agendamento
 * @param {string} appointmentId - ID do agendamento
 * @returns {Promise<object>} Agendamento atualizado com token
 */
export const ensureAppointmentHasToken = async (appointmentId) => {
  try {
    const appointmentRef = firestoreDoc(db, `salons/${appointmentId.split('/')[0]}/appointments/${appointmentId}`);
    const appointmentDoc = await getDoc(appointmentRef);
    
    if (!appointmentDoc.exists()) {
      throw new Error('Agendamento não encontrado');
    }
    
    const appointmentData = appointmentDoc.data();
    
    // Se já tem um token válido, retorna o agendamento
    if (appointmentData.accessToken) {
      // Verifica se o token está dentro da validade (48 horas)
      const tokenCreatedAt = appointmentData.tokenCreatedAt?.toDate();
      if (tokenCreatedAt && (new Date() - tokenCreatedAt) < 48 * 60 * 60 * 1000) {
        return { id: appointmentId, ...appointmentData };
      }
    }
    
    // Gera novo token
    const token = generateAppointmentToken(appointmentId);
    
    // Atualiza agendamento com o novo token
    await updateDoc(appointmentRef, {
      accessToken: token,
      tokenCreatedAt: Timestamp.now()
    });
    
    // Retorna agendamento atualizado
    return { 
      id: appointmentId, 
      ...appointmentData, 
      accessToken: token, 
      tokenCreatedAt: new Date() 
    };
  } catch (error) {
    console.error('Erro ao gerar token para agendamento:', error);
    throw error;
  }
};

/**
 * Valida um token de agendamento
 * @param {string} appointmentId - ID do agendamento
 * @param {string} token - Token a ser validado
 * @returns {Promise<object|null>} Agendamento se token válido, null se inválido
 */
export const validateAppointmentToken = async (appointmentId, token) => {
  try {
    const appointmentRef = firestoreDoc(db, `salons/${appointmentId.split('/')[0]}/appointments/${appointmentId}`);
    const appointmentDoc = await getDoc(appointmentRef);
    
    if (!appointmentDoc.exists()) {
      return null;
    }
    
    const appointmentData = appointmentDoc.data();
    
    // Verifica se o token corresponde
    if (appointmentData.accessToken !== token) {
      return null;
    }
    
    // Verifica se o token está dentro da validade (48 horas)
    const tokenCreatedAt = appointmentData.tokenCreatedAt?.toDate();
    if (!tokenCreatedAt || (new Date() - tokenCreatedAt) > 48 * 60 * 60 * 1000) {
      return null;
    }
    
    return { id: appointmentId, ...appointmentData };
  } catch (error) {
    console.error('Erro ao validar token:', error);
    return null;
  }
};

/**
 * Salva um bloqueio de horário no Firestore
 * @param {string} salonId - ID do salão
 * @param {Object} blockingData - Dados do bloqueio de horário
 * @returns {Promise<object>} - Dados do bloqueio salvo com ID gerado
 */
export const saveTimeBlocking = async (salonId, blockingData) => {
  try {
    if (!salonId) {
      throw new Error('ID do salão é obrigatório');
    }

    // Converter data para string se for objeto Date
    const date = blockingData.date instanceof Date 
      ? format(blockingData.date, 'yyyy-MM-dd')
      : blockingData.date;

    // Preparar o objeto de bloqueio
    const blockingDoc = {
      data: date,
      horarioInicio: blockingData.startTime,
      horarioFim: blockingData.endTime,
      titulo: blockingData.title || 'Horário Bloqueado',
      profissionalId: blockingData.resourceId || null, // null significa todos os profissionais
      tipo: 'bloqueio',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Salvar no Firestore
    const blockingsRef = collection(db, `salons/${salonId}/timeBlockings`);
    const docRef = await addDoc(blockingsRef, blockingDoc);
    
    return {
      id: docRef.id,
      ...blockingDoc,
    };
  } catch (error) {
    console.error('Erro ao salvar bloqueio de horário:', error);
    throw error;
  }
};

/**
 * Busca bloqueios de horário para uma data específica
 * @param {string} salonId - ID do salão
 * @param {string} date - Data no formato YYYY-MM-DD
 * @param {string} professionalId - ID do profissional (opcional)
 * @returns {Promise<Array>} - Lista de bloqueios de horário
 */
export const getTimeBlockings = async (salonId, date, professionalId = null) => {
  try {
    if (!salonId || !date) {
      throw new Error('ID do salão e data são obrigatórios');
    }

    console.log(`[availability.js] Buscando bloqueios para: salonId=${salonId}, date=${date}, professionalId=${professionalId || 'todos'}`);

    // Criar referência para a coleção de bloqueios
    const blockingsRef = collection(db, `salons/${salonId}/timeBlockings`);
    
    // Filtrar por data - sempre retorna TODOS os bloqueios para a data
    // A filtragem por profissional acontecerá depois de buscar os dados
    let blockingQuery = query(blockingsRef, where('data', '==', date));
    
    const blockingSnap = await getDocs(blockingQuery);
    
    // Converter snapshots para array de objetos
    return blockingSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Erro ao buscar bloqueios de horário:', error);
    return [];
  }
};

/**
 * Verifica se um horário está disponível para agendamento
 * @param {string} salonId - ID do salão
 * @param {string} date - Data no formato YYYY-MM-DD
 * @param {string} time - Horário no formato HH:MM
 * @param {string} professionalId - ID do profissional
 * @param {Array} serviceIds - IDs dos serviços selecionados
 * @param {string} excludeAppointmentId - ID do agendamento a ser excluído da verificação (útil para remarcações)
 * @returns {Promise<{available: boolean, conflicts: Array, totalDuration: number}>} - Resultado da verificação
 */
export const checkTimeSlotAvailability = async (
  salonId,
  date,
  time,
  professionalId,
  serviceIds,
  excludeAppointmentId = null
) => {
  try {
    // Buscar todos os agendamentos para a data selecionada
    const appointmentsForDate = await getAppointments(salonId, {
      startDate: date,
      endDate: date,
      stringDateFormat: true
    });
    
    // Filtrar apenas agendamentos não cancelados
    const activeAppointments = appointmentsForDate.filter(app => 
      !app.cancelado && 
      (excludeAppointmentId ? app.id !== excludeAppointmentId : true)
    );
    
    // Filtrar agendamentos do profissional selecionado
    const professionalAppointments = activeAppointments.filter(app => {
      // Verificar se o campo profissional existe e não é null
      if (!app.profissional) return false;
      
      const profId = typeof app.profissional === 'object' ? 
        app.profissional.id : app.profissional;
      return profId === professionalId;
    });
    
    // Buscar bloqueios de horário para esta data e profissional
    const timeBlockings = await getTimeBlockings(salonId, date, professionalId);
    
    // Buscar detalhes dos serviços
    const servicesRef = collection(db, `salons/${salonId}/services`);
    const servicesSnapshot = await getDocs(servicesRef);
    const servicesData = servicesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Calcular duração total dos serviços selecionados
    const selectedServices = servicesData.filter(service => serviceIds.includes(service.id));
    const totalDuration = selectedServices.reduce((total, service) => 
      total + (service.duracao || 30), 0);
    
    // Verificar se permite sobreposição
    const salonRef = firestoreDoc(db, `salons/${salonId}`);
    const salonSnapshot = await getDoc(salonRef);
    const salonData = salonSnapshot.data() || {};
    const allowsOverlap = salonData.allowsOverlap === true;
    
    // Converter hora de início para minutos desde o início do dia
    const [hours, minutes] = time.split(':').map(Number);
    const timeInMinutes = hours * 60 + minutes;
    
    // Calcular hora de término do novo agendamento
    const endTimeInMinutes = timeInMinutes + totalDuration;
    
    // Verificar conflitos com agendamentos
    const conflicts = [];
    
    // Verificar conflitos com bloqueios de horário
    // Filtrar apenas bloqueios relevantes para o profissional específico
    const relevantBlockings = timeBlockings.filter(block => 
      block.profissionalId === null || block.profissionalId === professionalId
    );

    // Verificar apenas os bloqueios relevantes
    for (const blocking of relevantBlockings) {
      if (isWithinBlockedTime(timeInMinutes, endTimeInMinutes, blocking)) {
        conflicts.push({
          blockingId: blocking.id,
          title: blocking.titulo,
          startTime: blocking.horarioInicio,
          endTime: blocking.horarioFim,
          type: 'blocking'
        });
      }
    }
    
    // Se permite sobreposição, está disponível mesmo com conflitos de agendamentos
    // Porém, bloqueios de horário sempre prevalecem, independente da configuração de sobreposição
    const hasBlockingConflicts = conflicts.some(conflict => conflict.type === 'blocking');
    const hasAppointmentConflicts = conflicts.some(conflict => conflict.type === 'appointment');
    
    // Horário disponível se:
    // - Não tiver conflitos com bloqueios E
    // - (Permitir sobreposição OU não tiver conflitos com agendamentos)
    const available = !hasBlockingConflicts && (allowsOverlap || !hasAppointmentConflicts);
    
    return {
      available,
      conflicts,
      totalDuration,
      allowsOverlap,
      hasBlockingConflicts,
      hasAppointmentConflicts
    };
  } catch (error) {
    console.error('Erro ao verificar disponibilidade de horário:', error);
    throw error;
  }
};

/**
 * Função auxiliar para verificar se um profissional está disponível em um horário específico
 * @param {string} professionalId - ID do profissional
 * @param {string} timeString - Horário a verificar
 * @param {Array} appointments - Lista de agendamentos
 * @param {Array} services - Lista de serviços
 * @param {number} serviceDuration - Duração total dos serviços
 * @returns {Object} - Status de disponibilidade do profissional
 */
const checkProfessionalAvailability = (
  professionalId, 
  timeString, 
  appointments, 
  services,
  serviceDuration
) => {
  // Filtrar apenas agendamentos deste profissional e que não estejam cancelados
  const profAppointments = appointments.filter(app => {
    const appProfId = typeof app.profissional === 'object' 
      ? (app.profissional?.id || null) 
      : app.profissional;
    
    // Verificar se o agendamento pertence a este profissional e NÃO está cancelado
    return appProfId === professionalId && app.cancelado !== true;
  });
  
  // Verificar conflitos específicos para este profissional
  const conflicts = checkConflictsAtTime(
    profAppointments,
    timeString,
    services,
    serviceDuration
  );
  
  return {
    id: professionalId,
    available: conflicts.length === 0,
    conflicts
  };
};

/**
 * Busca horários disponíveis para um profissional em uma data específica
 * Otimizado para usar o sistema de cache do React Query
 * 
 * @param {string} salonId - ID do salão
 * @param {string} date - Data no formato YYYY-MM-DD
 * @param {string} professionalId - ID do profissional
 * @param {Array} serviceIds - IDs dos serviços
 * @param {string} excludeAppointmentId - ID do agendamento a excluir da verificação
 * @returns {Promise<Array>} - Lista de slots de horário
 */
export const getAvailableTimeSlots = async (
  salonId,
  date,
  professionalId,
  serviceIds,
  excludeAppointmentId = null
) => {
  try {
    // Log detalhado dos parâmetros recebidos
    console.log(`[getAvailableTimeSlots] INICIANDO com parâmetros:`, {
      salonId,
      date,
      professionalId: professionalId || 'não especificado',
      serviceIds: serviceIds?.length || 0,
      excludeAppointmentId
    });
    
    // Validar parâmetros de entrada - tornar professionalId opcional
    if (!salonId || !date || !serviceIds || !Array.isArray(serviceIds) || serviceIds.length === 0) {
      console.warn('getAvailableTimeSlots: Parâmetros inválidos', { salonId, date, professionalId, serviceIds });
      return [];
    }
    
    // Buscar configurações do salão para horários de funcionamento
    const salonRef = firestoreDoc(db, `salons/${salonId}`);
    const salonSnapshot = await getDoc(salonRef);
    const salonData = salonSnapshot.data() || {};
    
    // Obter configurações de businessHours ou usar valores padrão
    const DEFAULT_BUSINESS_HOURS = {
      monday: { isOpen: true, intervals: [{ start: '09:00', end: '18:00' }] },
      tuesday: { isOpen: true, intervals: [{ start: '09:00', end: '18:00' }] },
      wednesday: { isOpen: true, intervals: [{ start: '09:00', end: '18:00' }] },
      thursday: { isOpen: true, intervals: [{ start: '09:00', end: '18:00' }] },
      friday: { isOpen: true, intervals: [{ start: '09:00', end: '18:00' }] },
      saturday: { isOpen: true, intervals: [{ start: '09:00', end: '14:00' }] },
      sunday: { isOpen: false, intervals: [{ start: '09:00', end: '18:00' }] }
    };
    
    const businessHours = salonData.businessHours || DEFAULT_BUSINESS_HOURS;
    
    // Verificar se o salão está aberto na data especificada
    const dateObj = new Date(date);
    const dayOfWeek = dateObj.getDay(); // 0 = domingo, 1 = segunda, ..., 6 = sábado
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayConfig = businessHours[dayNames[dayOfWeek]] || { isOpen: false, intervals: [] };
    
    if (!dayConfig.isOpen) {
      console.log(`[getAvailableTimeSlots] Salão fechado em ${date} (${dayNames[dayOfWeek]})`);
      return [];
    }
    
    // Buscar agendamentos para esta data
    const appointmentsRef = collection(db, `salons/${salonId}/appointments`);
    const q = query(
      appointmentsRef,
      where('data', '==', date)
    );
    
    const appointmentsSnapshot = await getDocs(q);
    let appointments = appointmentsSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        profissional: typeof data.profissional === 'object' 
          ? data.profissional.id || data.profissional 
          : data.profissional
      };
    });
    
    console.log(`[getAvailableTimeSlots] Encontrados ${appointments.length} agendamentos para data ${date}`);
    
    // Filtrar agendamentos cancelados
    appointments = appointments.filter(appointment => !appointment.cancelado);
    
    // CRÍTICO: Log para verificar se o excludeAppointmentId está sendo processado
    if (excludeAppointmentId) {
      console.log(`[getAvailableTimeSlots] Verificando excludeAppointmentId=${excludeAppointmentId}`);
      // Contar quantos agendamentos têm este ID antes da filtragem
      const countBeforeFilter = appointments.filter(a => a.id === excludeAppointmentId).length;
      console.log(`[getAvailableTimeSlots] Encontrados ${countBeforeFilter} agendamentos com ID=${excludeAppointmentId} antes de filtrar`);
      
      // Verificar se os IDs dos agendamentos estão no formato esperado
      if (countBeforeFilter === 0) {
        console.log('IDs dos agendamentos disponíveis:', appointments.map(a => a.id));
      }
    }
    
    // Remover o agendamento excluído (para remarcações)
    if (excludeAppointmentId) {
      const countBefore = appointments.length;
      appointments = appointments.filter(appointment => appointment.id !== excludeAppointmentId);
      const countAfter = appointments.length;
      
      // Log crítico para verificar se o appointmentId foi excluído corretamente
      console.log(`[getAvailableTimeSlots] Exclusão de agendamento: antes=${countBefore}, depois=${countAfter}, removidos=${countBefore - countAfter}`);
      
      if (countBefore === countAfter) {
        console.warn(`[getAvailableTimeSlots] AVISO: Agendamento com ID=${excludeAppointmentId} não foi encontrado para exclusão!`);
      }
    }
    
    // Filtrar apenas agendamentos para o profissional selecionado, se especificado
    let professionalAppointments = appointments;
    if (professionalId) {
      professionalAppointments = appointments.filter(appointment => 
        appointment.profissional === professionalId ||
        (appointment.profissional && appointment.profissional.id === professionalId)
      );
    }
    
    // Log de debug
    console.log(`[getAvailableTimeSlots] ${professionalId ? 'Profissional ' + professionalId : 'Todos profissionais'} tem ${professionalAppointments.length} agendamentos não-cancelados em ${date}`);
    
    // Buscar bloqueios de horário para esta data e profissional
    const timeBlockings = await getTimeBlockings(salonId, date, professionalId);
    
    console.log(`[getAvailableTimeSlots] Encontrados ${timeBlockings.length} bloqueios para ${date}, profissional: ${professionalId || 'todos'}`);
    
    // Filtrar bloqueios relevantes para este profissional
    // Se temos um profissional específico, considerar bloqueios gerais E bloqueios específicos desse profissional
    // Se não, considerar apenas bloqueios gerais
    const profBlockings = professionalId 
      ? timeBlockings.filter(block => block.profissionalId === null || block.profissionalId === professionalId)
      : timeBlockings.filter(block => block.profissionalId === null);
    
    console.log(`[getAvailableTimeSlots] Usando ${profBlockings.length} bloqueios relevantes para ${professionalId || 'todos'}`);
    
    // Log detalhado de cada bloqueio para depuração
    profBlockings.forEach(block => {
      console.log(`[getAvailableTimeSlots] Aplicando bloqueio: prof=${block.profissionalId || 'todos'}, horário=${block.horarioInicio}-${block.horarioFim}, título=${block.titulo}`);
    });
    
    // Gerar slots de horário baseados nos intervalos configurados para este dia
    const timeSlots = [];
    
    // Verificar se há intervalos configurados, caso contrário usar um intervalo padrão
    const intervals = dayConfig.intervals && dayConfig.intervals.length > 0
      ? dayConfig.intervals
      : [{ start: '09:00', end: '18:00' }];
    
    // Buscar todos os serviços selecionados
    const servicesRef = collection(db, `salons/${salonId}/services`);
    const servicesSnapshot = await getDocs(servicesRef);
    const allServices = servicesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filtrar apenas os serviços selecionados
    const services = allServices.filter(service => 
      serviceIds.includes(service.id)
    );
    
    console.log(`[getAvailableTimeSlots] Usando ${services.length} serviços, total ${allServices.length} disponíveis`);
    
    // Se não encontrou nenhum dos serviços, retornar lista vazia
    if (services.length === 0) {
      console.warn(`[getAvailableTimeSlots] Nenhum serviço encontrado para os IDs fornecidos: ${serviceIds.join(', ')}`);
      return [];
    }
    
    // Calcular duração total dos serviços (em minutos)
    const totalServiceDuration = services.reduce((total, service) => {
      return total + (service.duracao || 30);
    }, 0);
    
    console.log(`[getAvailableTimeSlots] Duração total dos serviços: ${totalServiceDuration} minutos`);
    
    // Se for solicitado um profissional específico
    // devemos verificar sua disponibilidade para cada slot
    let allProfessionals = [];
    
    if (!professionalId) {
      // Buscar todos os profissionais
      const professionalsRef = collection(db, `salons/${salonId}/professionals`);
      const professionalsSnapshot = await getDocs(professionalsRef);
      allProfessionals = professionalsSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(prof => prof.ativo !== false);  // Filtrar inativos
      
      console.log(`[getAvailableTimeSlots] Considerando ${allProfessionals.length} profissionais ativos`);
    }
    
    // Intervalo de tempo entre slots (geralmente 30 minutos)
    const timeSlotInterval = salonData.timeSlotInterval || 30;
    
    // Para cada intervalo configurado, gerar slots
    for (const interval of intervals) {
      // Converter horários de início e fim para minutos desde o início do dia
      const intervalStartMinutes = convertTimeToMinutes(interval.start);
      const intervalEndMinutes = convertTimeToMinutes(interval.end);
      
      // Gerar slots em intervalos regulares
      for (let minutes = intervalStartMinutes; minutes < intervalEndMinutes; minutes += timeSlotInterval) {
        const timeString = convertMinutesToTimeString(minutes);
        
        // Calcular o horário de término baseado na duração total dos serviços
        const endMinutes = minutes + totalServiceDuration;
        const endTimeString = convertMinutesToTimeString(endMinutes);
        
        // Verificar se excede o horário de funcionamento
        const exceedsBusinessHours = endMinutes > intervalEndMinutes;
        
        // Verificar se está em um período bloqueado
        const isBlocked = profBlockings.some(block => 
          isWithinBlockedTime(timeString, block.horarioInicio, block.horarioFim)
        );
        
        // Verificar conflitos com outros agendamentos
        const conflicts = checkConflictsAtTime(
          timeString,
          endTimeString,
          professionalAppointments,
          services
        );
        
        // Determinar disponibilidade de profissionais para este horário
        let professionals = [];
        
        // Se temos um profissional específico
        if (professionalId) {
          professionals = [{
            id: professionalId,
            available: !isBlocked && !exceedsBusinessHours && conflicts.length === 0
          }];
        } 
        // Se estamos verificando todos os profissionais
        else if (allProfessionals.length > 0) {
          professionals = allProfessionals.map(prof => {
            const profAvailability = checkProfessionalAvailability(
              prof.id,
              timeString,
              appointments,
              services,
              totalServiceDuration
            );
            
            return {
              id: prof.id,
              nome: prof.nome,
              foto: prof.foto,
              available: !isBlocked && !exceedsBusinessHours && profAvailability.available,
              conflicts: profAvailability.conflicts
            };
          });
        }
        
        // Um slot está genericamente disponível se:
        // 1. Não excede o horário de funcionamento E
        // 2. Não está bloqueado E
        // 3. Não tem conflitos OU pelo menos um profissional está disponível
        const hasAvailableProfessional = professionals.some(p => p.available);
        const available = !exceedsBusinessHours && !isBlocked && 
                        (conflicts.length === 0 || hasAvailableProfessional);
        
        timeSlots.push({
          time: timeString,
          available,
          hasConflicts: conflicts.length > 0 || isBlocked,
          conflicts,
          isBlocked,
          endTime: convertMinutesToTimeString(endMinutes), // Adicionar horário de término para referência
          professionals // Adicionar array de profissionais disponíveis
        });
      }
    }
    
    // Filtrar horários que já passaram se for hoje
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    
    let filteredTimeSlots = timeSlots;
    
    if (date === today) {
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinute;
      
      filteredTimeSlots = timeSlots.filter(slot => {
        const [slotHour, slotMinute] = slot.time.split(':').map(Number);
        const slotTimeInMinutes = slotHour * 60 + slotMinute;
        return slotTimeInMinutes > currentTimeInMinutes;
      });
      
      console.log(`[getAvailableTimeSlots] Filtrados horários passados: ${timeSlots.length} -> ${filteredTimeSlots.length}`);
    }
    
    // Log final com o número de slots disponíveis
    const availableCount = filteredTimeSlots.filter(slot => slot.available).length;
    console.log(`[getAvailableTimeSlots] CONCLUÍDO: Total=${filteredTimeSlots.length}, Disponíveis=${availableCount}, ExcludeId=${excludeAppointmentId || 'nenhum'}`);
    
    return filteredTimeSlots;
  } catch (error) {
    console.error('Erro em getAvailableTimeSlots:', error);
    return [];
  }
};

/**
 * Busca todos os serviços de um salão de uma vez
 * @param {string} salonId - ID do salão
 * @returns {Promise<Array>} - Lista de serviços
 */
export const getAllServices = async (salonId) => {
  try {
    const servicesRef = collection(db, `salons/${salonId}/services`);
    const servicesSnapshot = await getDocs(servicesRef);
    return servicesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Erro ao buscar serviços:', error);
    return [];
  }
};

/**
 * Busca todos os profissionais de um salão de uma vez
 * @param {string} salonId - ID do salão
 * @returns {Promise<Array>} - Lista de profissionais
 */
export const getAllProfessionals = async (salonId) => {
  try {
    const professionalsRef = collection(db, `salons/${salonId}/professionals`);
    const professionalsSnapshot = await getDocs(professionalsRef);
    return professionalsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Erro ao buscar profissionais:', error);
    return [];
  }
};

/**
 * Busca configurações do salão
 * @param {string} salonId - ID do salão
 * @returns {Promise<Object>} - Configurações do salão
 */
export const getSalonConfig = async (salonId) => {
  try {
    const salonRef = firestoreDoc(db, `salons/${salonId}`);
    const salonSnapshot = await getDoc(salonRef);
    return salonSnapshot.data() || {};
  } catch (error) {
    console.error('Erro ao buscar configurações do salão:', error);
    return {};
  }
};

/**
 * Busca dados de disponibilidade para um intervalo de datas
 * @param {string} salonId - ID do salão
 * @param {string} startDate - Data inicial no formato YYYY-MM-DD
 * @param {string} endDate - Data final no formato YYYY-MM-DD
 * @param {string} professionalId - ID do profissional (opcional)
 * @returns {Promise<Object>} - Mapa de disponibilidade por data/profissional
 */
export const getAvailabilityForDateRange = async (
  salonId,
  startDate,
  endDate,
  professionalId = null
) => {
  try {
    console.log(`[getAvailabilityForDateRange] Buscando disponibilidade de ${startDate} a ${endDate}`);
    
    // Filtrar agendamentos cancelados
    const appointmentFilters = { 
      startDate, 
      endDate, 
      stringDateFormat: true,
      canceled: false // Excluir cancelados
    };
    
    // Buscar todos os dados necessários em paralelo
    const [appointments, services, salonConfig, professionals] = await Promise.all([
      // Buscar agendamentos para o período
      getAppointments(salonId, appointmentFilters),
      
      // Buscar todos os serviços
      getAllServices(salonId),
      
      // Buscar configuração do salão
      getSalonConfig(salonId),
      
      // Buscar profissionais (caso não tenha sido especificado um)
      professionalId ? null : getAllProfessionals(salonId)
    ]);
    
    // Filtrar agendamentos com profissional nulo
    const validAppointments = appointments.filter(app => app.profissional != null);
    
    if (validAppointments.length !== appointments.length) {
      console.warn(`[getAvailabilityForDateRange] Ignorados ${appointments.length - validAppointments.length} agendamentos sem profissional`);
    }
    
    console.log(`[getAvailabilityForDateRange] Dados carregados: ${validAppointments.length} agendamentos, ${services.length} serviços, ${professionals ? professionals.length : 0} profissionais`);
    
    // Extrair configurações relevantes
    const allowsOverlap = salonConfig.allowsOverlap === true;
    const timeSlotInterval = salonConfig.timeSlotInterval || 30; // em minutos
    
    // Valores padrão para o caso de não haver businessHours configurado
    const DEFAULT_BUSINESS_HOURS = {
      monday: { isOpen: true, intervals: [{ start: '09:00', end: '18:00' }] },
      tuesday: { isOpen: true, intervals: [{ start: '09:00', end: '18:00' }] },
      wednesday: { isOpen: true, intervals: [{ start: '09:00', end: '18:00' }] },
      thursday: { isOpen: true, intervals: [{ start: '09:00', end: '18:00' }] },
      friday: { isOpen: true, intervals: [{ start: '09:00', end: '18:00' }] },
      saturday: { isOpen: true, intervals: [{ start: '09:00', end: '14:00' }] },
      sunday: { isOpen: false, intervals: [{ start: '09:00', end: '18:00' }] }
    };
    
    // Usar as configurações de businessHours ou valores padrão
    const businessHours = salonConfig.businessHours || DEFAULT_BUSINESS_HOURS;
    
    // Mapeamento de dias da semana
    const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    
    // Log para verificar a configuração de businessHours
    console.log(`[getAvailabilityForDateRange] Configuração de businessHours:`, JSON.stringify(businessHours));
    
    // Construir intervalo de datas
    const dates = getDatesBetween(startDate, endDate);
    
    // Criar mapa de disponibilidade
    const availabilityMap = {};
    
    // Para cada data no intervalo
    for (const date of dates) {
      availabilityMap[date] = {};
      
      // Buscar bloqueios para esta data (apenas uma vez por data)
      const dateBlockings = await getTimeBlockings(salonId, date);
      
      // Definir quais profissionais verificar
      const profsToCheck = professionalId 
        ? [{ id: professionalId }] 
        : (professionals || []);
      
      // Determinar dia da semana para esta data
      // Usamos o formato YYYY-MM-DD para evitar problemas de timezone
      const [year, month, day] = date.split('-').map(Number);
      const dateObj = new Date(year, month - 1, day); // mês em JS é 0-indexed
      const dayOfWeekIndex = dateObj.getDay(); // 0=domingo, 1=segunda, etc.
      const dayOfWeek = DAYS_OF_WEEK[dayOfWeekIndex];
      
      // Verificar se o salão está aberto neste dia
      const dayConfig = businessHours[dayOfWeek] || { isOpen: false, intervals: [] };
      
      // Log detalhado para debug
      console.log(`[getAvailabilityForDateRange] Data ${date} é ${dayOfWeek} (índice ${dayOfWeekIndex})`);
      console.log(`[getAvailabilityForDateRange] Configuração do dia: isOpen=${dayConfig.isOpen}, intervalos=${JSON.stringify(dayConfig.intervals)}`);
      
      // Para cada profissional
      for (const prof of profsToCheck) {
        // Filtrar agendamentos deste profissional nesta data
        const profAppointments = validAppointments.filter(app => {
          try {
            // Garantir que temos um app válido e uma data válida
            if (!app || !app.data) {
              return false;
            }
            
            // Obter ID do profissional do agendamento de forma segura
            const appProfId = typeof app.profissional === 'object' 
              ? (app.profissional && app.profissional.id ? app.profissional.id : null) 
              : app.profissional;
            
            // Verificar se corresponde à data e ao profissional
            return app.data === date && appProfId === prof.id;
          } catch (error) {
            console.error('Erro ao filtrar agendamento:', error, app);
            return false;
          }
        });
        
        // Filtrar bloqueios relevantes para este profissional
        // Se temos um profissional específico, considerar bloqueios gerais E bloqueios específicos desse profissional
        // Se não, considerar apenas bloqueios gerais
        const profBlockings = dateBlockings.filter(block => 
          block.profissionalId === null || block.profissionalId === prof.id
        );
        
        console.log(`[getAvailabilityForDateRange] Profissional ${prof.id}, data ${date}: ${profAppointments.length} agendamentos, ${profBlockings.length} bloqueios, dia ${dayOfWeek} aberto=${dayConfig.isOpen}`);
        
        // Gerar slots de horário
        const timeSlots = [];
        
        // Se o salão está fechado neste dia, não adicionar slots
        if (!dayConfig.isOpen) {
          availabilityMap[date][prof.id] = timeSlots;
          continue;
        }
        
        // Se não há intervalos configurados, use um intervalo padrão
        const intervals = dayConfig.intervals && dayConfig.intervals.length > 0
          ? dayConfig.intervals
          : [{ start: '09:00', end: '18:00' }];
        
        console.log(`[getAvailabilityForDateRange] Processando ${intervals.length} intervalos para ${dayOfWeek}`);
        
        // Para cada intervalo configurado neste dia da semana
        for (const interval of intervals) {
          // Log de cada intervalo para debug
          console.log(`[getAvailabilityForDateRange] Intervalo: ${interval.start} - ${interval.end}`);
          
          // Converter horários para minutos para facilitar cálculos
          const intervalStartMinutes = convertTimeToMinutes(interval.start || '09:00');
          const intervalEndMinutes = convertTimeToMinutes(interval.end || '18:00');
          
          // Para cada slot dentro deste intervalo
          for (let minutes = intervalStartMinutes; minutes < intervalEndMinutes; minutes += timeSlotInterval) {
            // Converter minutos para formato HH:MM
            const timeString = convertMinutesToTimeString(minutes);
            
            // Calcular horário de término (+ intervalo padrão para verificação)
            const endMinutes = minutes + timeSlotInterval;
            const endTimeString = convertMinutesToTimeString(endMinutes);
            
            // Verificar conflitos para este horário
            const conflicts = checkConflictsAtTime(
              profAppointments, 
              timeString, 
              services
              // Não passamos o parâmetro de duração, pois queremos verificar a disponibilidade do slot padrão
              // A duração específica será considerada posteriormente quando serviços forem selecionados
            );
            
            // Verificar se está dentro de algum bloqueio
            const isBlocked = profBlockings.some(block => 
              isWithinBlockedTime(minutes, endMinutes, block)
            );
            
            // Se estiver bloqueado, não está disponível independente de outras configurações
            const available = !isBlocked && (allowsOverlap || conflicts.length === 0);
            
            timeSlots.push({
              time: timeString,
              available,
              hasConflicts: conflicts.length > 0 || isBlocked,
              conflicts,
              isBlocked,
              endTime: convertMinutesToTimeString(endMinutes), // Adicionar horário de término para referência
              professionals // Adicionar array de profissionais disponíveis
            });
          }
        }
        
        // Adicionar slots ao mapa
        availabilityMap[date][prof.id] = timeSlots;
      }
    }
    
    return availabilityMap;
  } catch (error) {
    console.error('Erro ao buscar disponibilidade:', error);
    return {};
  }
};

/**
 * Busca todos os bloqueios de horário de um salão
 * @param {string} salonId - ID do salão
 * @returns {Promise<Array>} - Lista de bloqueios de horário
 */
export const getAllTimeBlockings = async (salonId) => {
  try {
    if (!salonId) {
      throw new Error('ID do salão é obrigatório');
    }

    // Criar referência para a coleção de bloqueios
    const blockingsRef = collection(db, `salons/${salonId}/timeBlockings`);
    
    // Buscar todos os bloqueios ordenados por data e horário
    const blockingQuery = query(blockingsRef, orderBy('data', 'desc'), orderBy('horarioInicio', 'asc'));
    const blockingSnap = await getDocs(blockingQuery);
    
    // Converter snapshots para array de objetos
    return blockingSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Erro ao buscar todos os bloqueios de horário:', error);
    return [];
  }
};

/**
 * Exclui um bloqueio de horário
 * @param {string} salonId - ID do salão
 * @param {string} blockingId - ID do bloqueio
 * @returns {Promise<boolean>} - Indica se a operação foi bem-sucedida
 */
export const deleteTimeBlocking = async (salonId, blockingId) => {
  try {
    if (!salonId || !blockingId) {
      throw new Error('ID do salão e ID do bloqueio são obrigatórios');
    }

    // Referência para o documento de bloqueio
    const blockingRef = firestoreDoc(db, `salons/${salonId}/timeBlockings/${blockingId}`);
    
    // Excluir documento
    await deleteDoc(blockingRef);
    
    return true;
  } catch (error) {
    console.error('Erro ao excluir bloqueio de horário:', error);
    return false;
  }
};

/**
 * Atualiza um bloqueio de horário existente
 * @param {string} salonId - ID do salão
 * @param {string} blockingId - ID do bloqueio
 * @param {Object} blockingData - Novos dados do bloqueio
 * @returns {Promise<Object|null>} - Dados atualizados ou null em caso de erro
 */
export const updateTimeBlocking = async (salonId, blockingId, blockingData) => {
  try {
    if (!salonId || !blockingId) {
      throw new Error('ID do salão e ID do bloqueio são obrigatórios');
    }

    // Converter data para string se for objeto Date
    const date = blockingData.date instanceof Date 
      ? format(blockingData.date, 'yyyy-MM-dd')
      : blockingData.date;

    // Preparar o objeto de bloqueio
    const blockingDoc = {
      data: date,
      horarioInicio: blockingData.startTime,
      horarioFim: blockingData.endTime,
      titulo: blockingData.title || 'Horário Bloqueado',
      profissionalId: blockingData.resourceId || null, // null significa todos os profissionais
      updatedAt: serverTimestamp()
    };

    // Referência para o documento de bloqueio
    const blockingRef = firestoreDoc(db, `salons/${salonId}/timeBlockings/${blockingId}`);
    
    // Atualizar documento
    await updateDoc(blockingRef, blockingDoc);
    
    return {
      id: blockingId,
      ...blockingDoc
    };
  } catch (error) {
    console.error('Erro ao atualizar bloqueio de horário:', error);
    throw error;
  }
};

/**
 * Verifica se há sobreposição entre bloqueios de horário
 * @param {string} salonId - ID do salão
 * @param {Object} blockingData - Dados do bloqueio a ser verificado
 * @param {string} excludeBlockingId - ID do bloqueio a ser excluído da verificação (útil para edições)
 * @returns {Promise<{hasOverlap: boolean, overlappingBlockings: Array}>} - Resultado da verificação
 */
export const checkBlockingOverlap = async (salonId, blockingData, excludeBlockingId = null) => {
  try {
    // Buscar bloqueios para a data especificada
    const blockings = await getTimeBlockings(salonId, blockingData.date);
    
    // Converter horários para minutos para facilitar comparação
    const startTime = blockingData.startTime.split(':').map(Number);
    const endTime = blockingData.endTime.split(':').map(Number);
    
    const startMinutes = startTime[0] * 60 + startTime[1];
    const endMinutes = endTime[0] * 60 + endTime[1];
    
    // Filtrar bloqueios relevantes
    // Se resourceId for null (todos profissionais), verificar todos os bloqueios
    // Se resourceId for específico, verificar apenas bloqueios deste profissional ou que afetam todos
    const relevantBlockings = blockings.filter(block => {
      // Excluir o próprio bloqueio em caso de edição
      if (excludeBlockingId && block.id === excludeBlockingId) {
        return false;
      }
      
      // Se o bloqueio sendo criado é para todos os profissionais, verificar sobreposição com todos
      if (!blockingData.resourceId) {
        return true;
      }
      
      // Se o bloqueio sendo criado é para um profissional específico, verificar sobreposição
      // apenas com bloqueios deste profissional ou bloqueios que afetam todos (profissionalId null)
      return block.profissionalId === null || block.profissionalId === blockingData.resourceId;
    });
    
    // Verificar sobreposição
    const overlappingBlockings = relevantBlockings.filter(block => {
      // Converter horários do bloqueio existente para minutos
      const blockStart = block.horarioInicio.split(':').map(Number);
      const blockEnd = block.horarioFim.split(':').map(Number);
      
      const blockStartMinutes = blockStart[0] * 60 + blockStart[1];
      const blockEndMinutes = blockEnd[0] * 60 + blockEnd[1];
      
      // Verificar sobreposição: o início do novo bloqueio é antes do fim do existente
      // E o fim do novo bloqueio é depois do início do existente
      return (startMinutes < blockEndMinutes && endMinutes > blockStartMinutes);
    });
    
    return {
      hasOverlap: overlappingBlockings.length > 0,
      overlappingBlockings
    };
  } catch (error) {
    console.error('Erro ao verificar sobreposição de bloqueios:', error);
    return { hasOverlap: false, overlappingBlockings: [] };
  }
};

/**
 * Converte um horário no formato HH:MM para minutos desde o início do dia
 * @param {string} timeString - Horário no formato HH:MM
 * @returns {number} - Minutos desde o início do dia
 */
const convertTimeToMinutes = (timeString) => {
  if (!timeString) return 0;
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Converte minutos desde o início do dia para um horário no formato HH:MM
 * @param {number} minutes - Minutos desde o início do dia
 * @returns {string} - Horário no formato HH:MM
 */
const convertMinutesToTimeString = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * Função auxiliar para verificar se um horário está dentro de um período bloqueado
 * @param {number} timeInMinutes - Horário de início em minutos
 * @param {number} endTimeInMinutes - Horário de término em minutos
 * @param {Object} blocking - Objeto de bloqueio com horarioInicio e horarioFim
 * @returns {boolean} - True se houver sobreposição
 */
const isWithinBlockedTime = (timeInMinutes, endTimeInMinutes, blocking) => {
  // Converter hora de início do bloqueio para minutos
  const blockStartMinutes = convertTimeToMinutes(blocking.horarioInicio);
  // Converter hora de fim do bloqueio para minutos
  const blockEndMinutes = convertTimeToMinutes(blocking.horarioFim);
  
  // Verificar sobreposição
  const isBlocked = (timeInMinutes < blockEndMinutes && endTimeInMinutes > blockStartMinutes);
  
  if (isBlocked) {
    console.log(`[isWithinBlockedTime] Bloqueio detectado: ${blocking.horarioInicio}-${blocking.horarioFim} bloqueia ${convertMinutesToTimeString(timeInMinutes)}-${convertMinutesToTimeString(endTimeInMinutes)}`);
  }
  
  return isBlocked;
};

/**
 * Verifica conflitos de agendamentos em um horário específico
 * @param {Array} appointments - Lista de agendamentos
 * @param {string} timeString - Horário a verificar no formato HH:MM
 * @param {Array} services - Lista de serviços para calcular duração
 * @param {number} serviceDuration - Duração total dos serviços selecionados (opcional)
 * @returns {Array} - Lista de conflitos encontrados
 */
const checkConflictsAtTime = (appointments, timeString, services, serviceDuration = null) => {
  // Converter horário para minutos
  const timeInMinutes = convertTimeToMinutes(timeString);
  
  // Lista de conflitos
  const conflicts = [];
  
  // Para cada agendamento
  for (const appointment of appointments) {
    // Ignorar agendamentos cancelados
    if (appointment.cancelado === true) {
      continue;
    }
    
    // Calcular duração dos serviços do agendamento
    const appointmentServiceIds = Array.isArray(appointment.servicos) 
      ? appointment.servicos.map(s => typeof s === 'object' ? s.id : s) 
      : [];
    
    const appointmentServices = services.filter(s => 
      appointmentServiceIds.includes(s.id)
    );
    
    const appointmentDuration = appointmentServices.reduce(
      (total, service) => total + (service.duracao || 30), 0
    );
    
    // Converter horário do agendamento para minutos
    const appTimeInMinutes = convertTimeToMinutes(appointment.horario);
    
    // Calcular horário de término do agendamento
    const appEndTimeInMinutes = appTimeInMinutes + appointmentDuration;
    
    // Usar a duração fornecida (real) ou um valor padrão
    const endTimeInMinutes = timeInMinutes + (serviceDuration || 30);
    
    // Verificar sobreposição entre o novo agendamento e o existente
    const overlaps = (
      (timeInMinutes < appEndTimeInMinutes && endTimeInMinutes > appTimeInMinutes)
    );
    
    if (overlaps) {
      conflicts.push({
        appointmentId: appointment.id,
        client: appointment.cliente,
        time: appointment.horario,
        endTime: convertMinutesToTimeString(appEndTimeInMinutes),
        duration: appointmentDuration
      });
    }
  }
  
  return conflicts;
};

/**
 * Gera array de datas entre startDate e endDate (inclusivo)
 * @param {string} startDate - Data inicial no formato YYYY-MM-DD
 * @param {string} endDate - Data final no formato YYYY-MM-DD
 * @returns {Array<string>} - Array de datas no formato YYYY-MM-DD
 */
const getDatesBetween = (startDate, endDate) => {
  const dates = [];
  const currentDate = new Date(startDate);
  const lastDate = new Date(endDate);
  
  // Ajustar para pegar até a data final inclusive
  lastDate.setDate(lastDate.getDate() + 1);
  
  while (currentDate < lastDate) {
    dates.push(currentDate.toISOString().split('T')[0]);
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return dates;
};

/**
 * Ajusta a disponibilidade de slots para serviços específicos
 * @param {Object} availabilityMap - Mapa de disponibilidade
 * @param {string} date - Data no formato YYYY-MM-DD
 * @param {string} professionalId - ID do profissional
 * @param {Array} serviceIds - IDs dos serviços selecionados
 * @param {Array} services - Lista completa de serviços
 * @returns {Array} - Lista atualizada de slots para os serviços específicos
 */
export const adjustAvailabilityForServices = (
  availabilityMap,
  date,
  professionalId,
  serviceIds,
  services
) => {
  // Se não temos dados para esta data/profissional
  if (!availabilityMap[date] || !availabilityMap[date][professionalId]) {
    return [];
  }
  
  // Slots base para o profissional nesta data
  const baseSlots = availabilityMap[date][professionalId];
  
  // Se não há serviços selecionados, retornar os slots base
  if (!serviceIds || serviceIds.length === 0) {
    return baseSlots;
  }
  
  // Calcular duração total dos serviços selecionados
  const selectedServices = services.filter(s => serviceIds.includes(s.id));
  const totalDuration = selectedServices.reduce(
    (total, service) => total + (service.duracao || 30), 0
  );
  
  // Ajustar slots para considerar a duração real
  return baseSlots.map(slot => {
    // Criar cópia do slot
    const adjustedSlot = { ...slot };
    
    // Verificar se o slot ainda está disponível com a nova duração
    if (slot.hasConflicts) {
      // Obter horário de início em minutos
      const startTimeInMinutes = convertTimeToMinutes(slot.time);
      
      // Calcular horário de término com a duração real
      const endTimeInMinutes = startTimeInMinutes + totalDuration;
      
      // Verificar se algum conflito impede este slot com a nova duração
      const invalidConflict = slot.conflicts.some(conflict => {
        const conflictStartMinutes = convertTimeToMinutes(conflict.time);
        const conflictEndMinutes = convertTimeToMinutes(conflict.endTime);
        
        // Verificar sobreposição mais precisa com a duração real
        return (
          (startTimeInMinutes < conflictEndMinutes && 
           endTimeInMinutes > conflictStartMinutes)
        );
      });
      
      // Atualizar disponibilidade se necessário
      if (invalidConflict && !slot.allowsOverlap) {
        adjustedSlot.available = false;
      }
    }
    
    return adjustedSlot;
  });
}; 